/**
 * eReg Delegation Log Page
 *
 * Native delegation of authority (DOA) management.
 * Tracks team members, delegated tasks, required documents, and signatures.
 */

import { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  AlertCircle,
  CheckCircle,
  FileText,
  X,
  UserPlus,
  Edit2,
  Save,
  PenTool,
  Download,
} from 'lucide-react';
import { useEreg } from '../contexts/EregContext';
import { useAuth } from '../../contexts/AuthContext';
import * as eregApi from '../lib/api';
import { PageHeader, RefreshButton, fieldCls, primaryBtnCls, secondaryBtnCls } from '../../ui';
import type { DelegationLog, DelegationTask, DelegationEntry, User } from '../types';

// Entry status badge component
function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending_cert_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    pending_signatures: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    ended: 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300',
    terminated: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  };

  const labels: Record<string, string> = {
    pending_cert_review: 'Pending Docs',
    pending_signatures: 'Pending Signatures',
    active: 'Active',
    ended: 'Ended',
    terminated: 'Terminated',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300'}`}>
      {labels[status] || status}
    </span>
  );
}

export function DelegationLogPage() {
  const { currentProtocol } = useEreg();
  const { user: authUser } = useAuth();
  const [delegationLog, setDelegationLog] = useState<DelegationLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [editingTasks, setEditingTasks] = useState(false);
  const [signOffEntry, setSignOffEntry] = useState<{ entry: DelegationEntry; role: 'team_member' | 'pi' } | null>(null);
  const [exporting, setExporting] = useState(false);

  const isPi = Boolean(
    authUser?.id && currentProtocol?.primary_investigator_id === authUser.id
  );

  useEffect(() => {
    if (currentProtocol?.id) {
      loadDelegationLog();
      loadRoles();
    }
  }, [currentProtocol?.id]);

  const loadDelegationLog = async () => {
    if (!currentProtocol?.id) return;
    setLoading(true);
    try {
      const log = await eregApi.getDelegationLog(currentProtocol.id);
      setDelegationLog(log);
      if (!log) {
        setShowSetupModal(true);
      }
    } catch (error) {
      console.error('Failed to load delegation log:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const roleList = await eregApi.getDelegationRoles();
      setRoles(roleList);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const handleSetupDelegationLog = async () => {
    if (!currentProtocol?.id) return;
    try {
      const log = await eregApi.createDelegationLog({
        protocol_id: currentProtocol.id,
      });
      setDelegationLog(log);
      setShowSetupModal(false);
      loadDelegationLog(); // Reload to get tasks
    } catch (error) {
      console.error('Failed to create delegation log:', error);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTaskNumbers = (entry: DelegationEntry, tasks: DelegationTask[]) => {
    const delegatedTasks = tasks.filter(t => entry.delegated_task_ids.includes(t.id));
    return delegatedTasks.map(t => t.task_number).sort((a, b) => a - b).join(', ');
  };

  const getMissingDocsCount = (entry: DelegationEntry) => {
    return entry.required_docs?.filter(d => d.status === 'missing').length || 0;
  };

  const handleExportPdf = async () => {
    if (!currentProtocol?.id) return;
    setExporting(true);
    try {
      const blob = await eregApi.exportDelegationLogPdf(currentProtocol.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `delegation-log-${currentProtocol.protocol_number || 'protocol'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export delegation log PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  if (!currentProtocol) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <AlertCircle size={40} className="mx-auto mb-2 text-amber-500 dark:text-amber-400" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No Study Selected</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Please select a study from the dashboard to view its delegation log.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading delegation log...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title="Delegation Log"
        subtitle={`${currentProtocol.protocol_number} - ${currentProtocol.protocol_title || 'Untitled Study'}`}
        actions={
          <>
            {delegationLog && (
              <button
                onClick={handleExportPdf}
                disabled={exporting}
                className={secondaryBtnCls}
              >
                <Download size={16} />
                <span>{exporting ? 'Exporting...' : 'Export PDF'}</span>
              </button>
            )}
            {delegationLog && (
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
              >
                <UserPlus size={18} />
                <span>Add Team Member</span>
              </button>
            )}
            <RefreshButton onClick={loadDelegationLog} loading={loading} />
          </>
        }
      />

      {/* Setup Modal */}
      {showSetupModal && !delegationLog && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <Users size={48} className="mx-auto mb-4 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
            Set Up Delegation Log
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            The delegation log tracks team members and their authorized study responsibilities.
            Click below to set up the delegation log with default tasks.
          </p>
          <button
            onClick={handleSetupDelegationLog}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
          >
            Set Up Delegation Log
          </button>
        </div>
      )}

      {/* Delegation Log Content */}
      {delegationLog && (
        <>
          {/* Tasks Section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <FileText size={20} className="text-primary-600 dark:text-primary-400" />
                Delegated Tasks
              </h2>
              <button
                onClick={() => setEditingTasks(!editingTasks)}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
              >
                {editingTasks ? <Save size={16} /> : <Edit2 size={16} />}
                {editingTasks ? 'Save' : 'Edit Tasks'}
              </button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(delegationLog.tasks || []).map((task) => (
                  <div
                    key={task.id}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg dark:bg-gray-900/40"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                      {task.task_number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{task.task_description}</p>
                      {task.required_document_types?.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Requires: {task.required_document_types.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Team Members Section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Users size={20} className="text-primary-600 dark:text-primary-400" />
                Study Team ({delegationLog.entries?.length || 0})
              </h2>
            </div>

            {!delegationLog.entries || delegationLog.entries.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <Users size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                <p>No team members added yet</p>
                <p className="text-sm mt-1">Add team members to assign delegated tasks</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Team Member
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Tasks
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Dates
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Docs
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Sign-offs
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                    {delegationLog.entries.map((entry) => {
                      const missingDocs = getMissingDocsCount(entry);
                      return (
                        <tr key={entry.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {entry.user?.full_name || entry.external_name || 'Unknown'}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {entry.user?.email || entry.external_email}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {entry.role === 'Other' ? entry.role_other : entry.role}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-200">
                              {getTaskNumbers(entry, delegationLog.tasks || [])}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className="text-gray-900 dark:text-gray-100">{formatDate(entry.start_date)}</div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {entry.end_date ? `to ${formatDate(entry.end_date)}` : 'Ongoing'}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <StatusBadge status={entry.status} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {missingDocs > 0 ? (
                              <span className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400">
                                <AlertCircle size={14} />
                                {missingDocs} missing
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                                <CheckCircle size={14} />
                                Complete
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col gap-1 text-xs">
                              {/* Team member sign-off */}
                              {entry.team_member_start_signature ? (
                                <span className="flex items-center gap-1 text-green-600 dark:text-green-400" title={`"${entry.team_member_start_signature.signature_meaning}" — ${new Date(entry.team_member_start_signature.signed_at).toLocaleString()}`}>
                                  <CheckCircle size={12} />
                                  Member: {entry.team_member_start_signature.signer_name}
                                </span>
                              ) : entry.status === 'pending_signatures' && authUser?.id && entry.user_id === authUser.id ? (
                                <button
                                  onClick={() => setSignOffEntry({ entry, role: 'team_member' })}
                                  className="inline-flex w-fit items-center gap-1 rounded-lg bg-primary-600 px-2 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                  <PenTool size={12} />
                                  Sign
                                </button>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">Member: not signed</span>
                              )}
                              {/* PI sign-off */}
                              {entry.pi_start_signature ? (
                                <span className="flex items-center gap-1 text-green-600 dark:text-green-400" title={`"${entry.pi_start_signature.signature_meaning}" — ${new Date(entry.pi_start_signature.signed_at).toLocaleString()}`}>
                                  <CheckCircle size={12} />
                                  PI: {entry.pi_start_signature.signer_name}
                                </span>
                              ) : entry.status === 'pending_signatures' && isPi ? (
                                <button
                                  onClick={() => setSignOffEntry({ entry, role: 'pi' })}
                                  className="inline-flex w-fit items-center gap-1 rounded-lg bg-primary-600 px-2 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                  <PenTool size={12} />
                                  Sign as PI
                                </button>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">PI: not signed</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Team Member Modal */}
      {showAddMemberModal && delegationLog && (
        <AddMemberModal
          delegationLog={delegationLog}
          roles={roles}
          siteId={currentProtocol.site_id}
          onClose={() => setShowAddMemberModal(false)}
          onSuccess={() => {
            setShowAddMemberModal(false);
            loadDelegationLog();
          }}
        />
      )}

      {/* Sign-off Modal */}
      {signOffEntry && delegationLog && (
        <SignOffModal
          delegationLogId={delegationLog.id}
          entry={signOffEntry.entry}
          role={signOffEntry.role}
          onClose={() => setSignOffEntry(null)}
          onSuccess={() => {
            setSignOffEntry(null);
            loadDelegationLog();
          }}
        />
      )}
    </div>
  );
}

// Sign-off Modal — password re-auth ceremony for delegation sign-offs
function SignOffModal({
  delegationLogId,
  entry,
  role,
  onClose,
  onSuccess,
}: {
  delegationLogId: string;
  entry: DelegationEntry;
  role: 'team_member' | 'pi';
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const memberName = entry.user?.full_name || entry.external_name || 'team member';
  // Preview only — the backend writes the authoritative meaning string
  // (backend/src/routes/ereg.js delegation sign route); keep in sync.
  const meaning = role === 'team_member'
    ? 'I accept responsibility for the delegated study tasks'
    : 'I authorize this delegation of study responsibilities';

  const handleSign = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await eregApi.signDelegationEntry(delegationLogId, entry.id, { password, role });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to sign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {role === 'team_member' ? 'Sign Delegation Entry' : 'Countersign as PI'}
          </h2>
        </div>
        <div className="space-y-4 p-4">
          <div className="rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {role === 'team_member' ? 'Your delegation entry' : `Delegation entry for ${memberName}`}
            </p>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              Meaning of signature: <span className="font-medium text-gray-700 dark:text-gray-300">"{meaning}"</span>
            </p>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            <div className="flex gap-2">
              <AlertCircle className="flex-shrink-0 text-amber-600 dark:text-amber-400" size={18} />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                This records a legally binding electronic signature. Enter your password to verify your identity.
              </p>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your account password"
              className={`w-full ${fieldCls}`}
              autoFocus
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSign}
              disabled={loading || !password}
              className={primaryBtnCls}
            >
              <PenTool size={14} />
              {loading ? 'Signing...' : 'Sign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Add Member Modal Component
function AddMemberModal({
  delegationLog,
  roles,
  siteId,
  onClose,
  onSuccess,
}: {
  delegationLog: DelegationLog;
  roles: string[];
  siteId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    user_id: '',
    external_name: '',
    external_email: '',
    role: '',
    role_other: '',
    delegated_task_ids: [] as string[],
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
  });
  const [isExternal, setIsExternal] = useState(false);
  const [siteUsers, setSiteUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    eregApi.getEligibleSigners(siteId)
      .then(setSiteUsers)
      .catch((err) => console.error('Failed to load site users:', err));
  }, [siteId]);

  const handleTaskToggle = (taskId: string) => {
    setFormData(prev => ({
      ...prev,
      delegated_task_ids: prev.delegated_task_ids.includes(taskId)
        ? prev.delegated_task_ids.filter(id => id !== taskId)
        : [...prev.delegated_task_ids, taskId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.role) {
      setError('Please select a role');
      return;
    }

    if (formData.delegated_task_ids.length === 0) {
      setError('Please select at least one task');
      return;
    }

    if (isExternal && (!formData.external_name || !formData.external_email)) {
      setError('Please enter name and email for external team member');
      return;
    }

    if (!isExternal && !formData.user_id) {
      setError('Please select the internal user');
      return;
    }

    setLoading(true);
    try {
      await eregApi.addDelegationEntry(delegationLog.id, {
        user_id: isExternal ? undefined : formData.user_id || undefined,
        external_name: isExternal ? formData.external_name : undefined,
        external_email: isExternal ? formData.external_email : undefined,
        role: formData.role,
        role_other: formData.role === 'Other' ? formData.role_other : undefined,
        delegated_task_ids: formData.delegated_task_ids,
        start_date: formData.start_date,
        end_date: formData.end_date || undefined,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add team member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add Team Member</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors duration-150 dark:hover:bg-gray-700">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Internal/External Toggle */}
          <div className="flex gap-4 p-3 bg-gray-50 rounded-lg dark:bg-gray-900/40">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!isExternal}
                onChange={() => setIsExternal(false)}
                className="text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Internal User</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={isExternal}
                onChange={() => setIsExternal(true)}
                className="text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">External (Sponsor/CRO)</span>
            </label>
          </div>

          {/* Internal user picker */}
          {!isExternal && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Team Member *
              </label>
              <select
                value={formData.user_id}
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                className={`w-full ${fieldCls}`}
              >
                <option value="">Select user...</option>
                {siteUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* External user fields */}
          {isExternal && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.external_name}
                  onChange={(e) => setFormData({ ...formData, external_name: e.target.value })}
                  className={`w-full ${fieldCls}`}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.external_email}
                  onChange={(e) => setFormData({ ...formData, external_email: e.target.value })}
                  className={`w-full ${fieldCls}`}
                  placeholder="john@sponsor.com"
                />
              </div>
            </>
          )}

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className={`w-full ${fieldCls}`}
            >
              <option value="">Select role...</option>
              {roles.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          {formData.role === 'Other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Specify Role
              </label>
              <input
                type="text"
                value={formData.role_other}
                onChange={(e) => setFormData({ ...formData, role_other: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>
          )}

          {/* Task Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Delegated Tasks *
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 dark:border-gray-700">
              {(delegationLog.tasks || []).map((task) => (
                <label key={task.id} className="flex items-start gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded transition-colors duration-150 dark:hover:bg-gray-700/50">
                  <input
                    type="checkbox"
                    checked={formData.delegated_task_ids.includes(task.id)}
                    onChange={() => handleTaskToggle(task.id)}
                    className="mt-1 rounded text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                  />
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium">{task.task_number}.</span> {task.task_description}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date (optional)
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <Plus size={18} />
                  <span>Add Team Member</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DelegationLogPage;
